/**
 * f16.1: "Commitment by Business Unit Group" -- Recharts, the one chart
 * library (adr-6). Colours arrive as a prop from `useChartPalette`; bar `i`
 * is `colors[i % colors.length]`, and from the sixth bar on it is drawn at
 * half opacity, exactly as the prototype draws "CU Others".
 *
 * Values are plotted as numbers (a chart has to), but every figure a person
 * READS -- axis, tooltip, legend -- is formatted from the server's decimal
 * string with its currency code.
 */

import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatMoney, type MoneyLike } from '@/routes/cycles/review/reviewFormat';

export interface GroupBarDatum {
  id: string;
  name: string;
  money: MoneyLike;
}

export interface CommitmentByGroupChartProps {
  groups: readonly GroupBarDatum[];
  currency: string;
  colors: readonly string[];
}

const COMPACT: Readonly<Record<string, string>> = { USD: '$', EUR: '€', GBP: '£' };

/** `$12.4M` for an axis tick -- the exact value is in the tooltip. */
export function compactAmount(value: number, currency: string): string {
  const symbol = COMPACT[currency] ?? `${currency} `;
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${symbol}${Math.round(abs / 1_000)}k`;
  return `${sign}${symbol}${Math.round(abs)}`;
}

/** Bar `i`'s fill: the palette wraps, and a wrapped bar is drawn at half opacity. */
export function barFill(colors: readonly string[], index: number): { fill: string; fillOpacity: number } {
  const size = Math.max(colors.length, 1);
  return { fill: colors[index % size] ?? 'currentColor', fillOpacity: index >= size ? 0.5 : 1 };
}

export function CommitmentByGroupChart({ groups, currency, colors }: CommitmentByGroupChartProps) {
  const data = groups.map((group) => ({ ...group, value: Number(group.money.amount) }));
  const size = Math.max(colors.length, 1);
  return (
    <>
      <div className="dash-chart" role="img" aria-label={`Commitment by Business Unit Group, in ${currency}`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <XAxis dataKey="name" hide />
            <YAxis
              width={64}
              tickLine={false}
              axisLine={false}
              tick={{ fill: 'currentColor', fontSize: 11 }}
              tickFormatter={(value: number) => compactAmount(value, currency)}
            />
            <Tooltip
              cursor={{ fillOpacity: 0.06 }}
              content={({ active, payload }) => {
                const datum = active ? (payload?.[0]?.payload as (typeof data)[number] | undefined) : undefined;
                if (!datum) return null;
                return (
                  <div className="dash-tooltip">
                    <div className="dash-tooltip-name">{datum.name}</div>
                    <div className="num" title={`${datum.money.amount} ${datum.money.currency_code}`}>
                      {formatMoney(datum.money)}
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {data.map((datum, index) => {
                const { fill, fillOpacity } = barFill(colors, index);
                return <Cell key={datum.id} fill={fill} fillOpacity={fillOpacity} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="legend mt-4">
        {groups.map((group, index) => (
          <span key={group.id} className="legend-item">
            <span
              className={`swatch swatch-${(index % size) + 1}${index >= size ? ' is-faded' : ''}`}
              aria-hidden="true"
            />
            {group.name}
          </span>
        ))}
      </div>
    </>
  );
}
